import type {TFunction} from "i18next";
import {PrimeIcons} from "primereact/api";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {ColumnGroup} from "primereact/columngroup";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import {InputTextarea} from "primereact/inputtextarea";
import {Message} from "primereact/message";
import {Row} from "primereact/row";
import {type ReactNode, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import type {RevaLanguage} from "@reva/shared/i18n";
import {formatPrice, formatValue} from "@reva/shared/number-format";
import {
    formatSectionIIOutputValue,
    isManualSectionIIEntry,
    LEASEHOLD_ENTRY_NAME,
    matchSectionIIResults,
    PREEMPTION_ENTRY_NAME,
    SECTION_II_ENTRY_PARAMS,
    SECTION_II_VALUE_FIELDS,
    SectionIICategory,
    type SectionIIEntryResult,
    splitSectionIIColumns,
    sumSectionIIEntryValues,
    withLiveLeaseholdEntries,
} from "@reva/shared/register";
import type {RevaSpecObjectInputRow} from "@reva/shared/reva-spec";

import {ColumnHeaderWithIcons} from "../../../component/paramInput/ColumnHeaderWithIcons";
import {CurrencyInputNumber} from "../../../component/paramInput/CurrencyInputNumber";
import {CellInputActionIcon} from "../../../component/paramInput/InputGrossFloorArea";
import InputTextField from "../../../component/paramInput/InputTextField";
import {useParameterValue} from "../../../component/paramInput/paramHooks";
import {ROW_ACTION_COLUMN_STYLE} from "../../../component/paramInput/RowDeleteIcon";
import {useLanguage, useSelectByCurrentLanguage} from "../../../hooks/languageHooks";
import {selectAllInitialValuationParams} from "../../../redux/objectParametersSlice";
import {selectReadOnly} from "../../../redux/persistenceSlice";
import {
    missingSectionIIRows,
    resetOrEmptySectionII,
    type SectionIIRow,
    saveSectionIIRows,
    selectCanResetSectionII,
    selectHasSectionIIPercentages,
} from "../../../redux/sectionIIThunk";
import type {RevaDispatch} from "../../../redux/store";
import {selectIndicationData} from "../../../redux/valueIndicationSlice";
import {getAppraisalValueDetails} from "../../../util/valueIndicationUtil";
import {RegisterOutputTable, VALUE_CELL_CLASS} from "./RegisterOutputTable";

const abt2SubtypeList = [
    "Ausschluss Aufhebung Eigentümergemeinschaft",
    "Baubeschränkung",
    "Brauereirecht",
    "Contracting",
    "Dauerwohnrecht/-nutzungsrecht nach WEG",
    "dingliches Wohnungsrecht nach BGB",
    "Duldung Photovoltaikanlage",
    "Duldung von Immissionen",
    "Energieversorgung",
    "Kabel-/Kanalrecht",
    "Leitungsrecht/-duldungsrecht",
    "Mitbenutzungsrecht für Garten/Müllplatz/Spielplatz",
    "Nutzungsbeschränkung",
    "sonstige Dienstbarkeit",
    "Stellplatznutzungsrecht",
    "Tankstellenrecht",
    "Traforecht",
    "Wege-/Geh-/Fahrrecht",
    "Wohnungsbelegungs-/Wohnungsbesetzungsrecht",
    "Grenzbebauung",
    "Überbaurecht",
    "Auflassungsvormerkung",
    "Auflassungsvormerkung (nicht für den Darlehensnehmer)",
    "sonstige Vormerkung",
    "Vorkaufsrecht",
    "Vormerkung wg. Erhöhung Erbbauzins",
    "Wiederkaufsrecht/Rück-AV",
    "Konkurs-/Insolvenz-/Zwangsversteigerungsvermerk",
    "Nacherbenvermerk",
    "Sanierungs-/Entwicklungsvermerk",
    "sonstige Verfügungsbeschränkung",
    "Verfügungsbeschränkung",
    "Zwangsverwaltungsvermerk",
    "Altenteil (Leibgedinge, ...)",
    "sonstige Reallast",
    "Nießbrauch",
    "Testamentsvollstreckungsvermerk",
].sort((a, b) => a.localeCompare(b, "de"));
const abt2TypeList = [
    "Beschränkte persönliche Dienstbarkeit",
    "Grunddienstbarkeit",
    "Vormerkung",
    "Sonstiges",
    "Reallast",
    "Nießbrauch",
    "Verfügungsbeschränkung",
].sort((a, b) => a.localeCompare(b, "de"));
const typeParameter = "section_ii_entry_type_de";
const subtypeParameter = "section_ii_entry_subtype_de";
const valueParameter = "section_ii_entry_value";
const parcelParameter = "register_abt2_parcel";
const nrParameter = "register_abt2_nr";
const textParameter = "register_abt2_text";
const noteParameter = "register_abt2_note";
const liveEntryFields: readonly string[] = SECTION_II_ENTRY_PARAMS;

/** Je eine Zeile für Marktwert und Beleihungswert eines Eintrags (#19602). */
type ValueLine = (typeof SECTION_II_VALUE_FIELDS)[keyof typeof SECTION_II_VALUE_FIELDS] & {
    key: keyof typeof SECTION_II_VALUE_FIELDS;
    categories: number[];
};

const VALUE_LINES: ValueLine[] = [
    {
        ...SECTION_II_VALUE_FIELDS.market,
        key: "market",
        categories: [SectionIICategory.VALUE_REDUCING, SectionIICategory.NOT_CONSIDERED],
    },
    {
        ...SECTION_II_VALUE_FIELDS.lending,
        key: "lending",
        categories: [
            SectionIICategory.VALUE_REDUCING,
            SectionIICategory.PRIOR_CHARGE,
            SectionIICategory.NOT_CONSIDERED,
        ],
    },
];

/** Ohne Angabe rechnet die AVM mit Basis 1 (lastenfreier Wert). */
const DEFAULT_PERCENTAGE_BASE = 1;
const PERCENTAGE_BASES = [1, 2];

type Abt2Row = SectionIIRow;

const NEW_ROW: Abt2Row = {newRow: true};

/**
 * Stabile React-Keys, damit Eingabefelder mit lokalem State (Text, Bemerkung, eingeklappter Wertabzug) bei ihrer Zeile
 * bleiben. Die manuellen Zeilen stehen vorn und haben keine eigene ID, ihre Keys laufen deshalb parallel zu den Daten
 * mit: Ein neuer Eintrag hängt hinten an, ein gelöschter nimmt seinen Key mit. Die neue Zeile bekommt nach jedem
 * Anlegen einen frischen Key, sonst bliebe die eben getippte Eingabe in ihr stehen (#19602-44).
 */
function useRowKeys(rows: Abt2Row[]) {
    const keys = useRef<number[]>([]);
    const next = useRef(0);
    const manualCount = rows.filter(isManualSectionIIEntry).length;
    while (keys.current.length < manualCount) keys.current.push(next.current++);
    keys.current.length = manualCount;
    return {
        keyOf(row: Abt2Row, index: number): string {
            if (row.newRow) return `new-${next.current}`;
            if (isManualSectionIIEntry(row)) return `row-${keys.current[index]}`;
            return row[subtypeParameter] === LEASEHOLD_ENTRY_NAME ? "leasehold" : "preemption";
        },
        remove(index: number) {
            keys.current.splice(index, 1);
        },
    };
}

/**
 * Anzeigereihenfolge der Eingabe: Erbbau-/Vorkaufsrecht-Zeilen oben, darunter die manuellen Einträge und die neue
 * Zeile, damit ein neuer Eintrag dort bleibt, wo er angelegt wurde (#19602-29). Die Daten behalten ihre Reihenfolge,
 * weil die AVM-Ergebnisse positionsgleich zugeordnet werden.
 */
function displayOrder(rows: Abt2Row[]): number[] {
    const indices = rows.map((_, index) => index);
    const isAuto = (row: Abt2Row) => !row.newRow && !isManualSectionIIEntry(row);
    return [...indices.filter((index) => isAuto(rows[index])), ...indices.filter((index) => !isAuto(rows[index]))];
}

/** Wirksame Kategorie einer Wertzeile; die Erbbau-Zeile übernimmt sie aus dem AVM-Ergebnis. */
function lineCategory(line: ValueLine, row: Abt2Row, result: SectionIIEntryResult | undefined): number {
    const category = isNotEditable(row, line.category) ? result?.[line.resultCategory] : row[line.category];
    return (category as number | null | undefined) ?? line.defaultCategory;
}

/** Wertneutral: in MW und BelW „nein“ und weder Wert noch Anteil erfasst, der Wertabzug startet dann eingeklappt. */
function isValueNeutral(row: Abt2Row, result: SectionIIEntryResult | undefined): boolean {
    return VALUE_LINES.every(
        (line) =>
            lineCategory(line, row, result) === SectionIICategory.NOT_CONSIDERED &&
            row[line.value] == null &&
            row[line.percentage] == null,
    );
}

interface RegisterSectionIIInputProps {
    columns: RevaSpecObjectInputRow[] | undefined;
    loading?: boolean;
    compact?: boolean;
}

export function RegisterSectionIIInput({columns, loading, compact}: RegisterSectionIIInputProps) {
    const {parameterValues: registerData} = useParameterValue("register_abt2", "REGISTER");
    const {parameterValues: abt2Values, onValueChange: setAbt2Values} = useParameterValue(valueParameter, "REGISTER");
    const initialValuationParams = useSelector(selectAllInitialValuationParams);
    const {onValueChange: setPreemptionRight} = useParameterValue("prop_preemption_right", "REGISTER");
    const indicationData = useSelector(selectIndicationData);
    const readOnly = useSelector(selectReadOnly) || !!compact;
    const canReset = useSelector(selectCanResetSectionII);
    // TODO #19602: Weiche für LIVE, nach dem AVM-Release entfernen
    const withPercentages = useSelector(selectHasSectionIIPercentages);
    const {t} = useTranslation();
    const language = useLanguage();
    const getByLanguage = useSelectByCurrentLanguage();
    const dispatch = useDispatch<RevaDispatch>();
    const appraisalDetails = getAppraisalValueDetails(indicationData);
    const abt2Data: Abt2Row[] = loading
        ? []
        : withLiveLeaseholdEntries(registerData?.activeParameterValue, appraisalDetails);
    const results = matchSectionIIResults(
        abt2Data,
        appraisalDetails?.section_ii_entries as SectionIIEntryResult[] | undefined,
    );
    const rows = readOnly ? abt2Data : [...abt2Data, NEW_ROW];
    const rowKeys = useRowKeys(abt2Data);
    const warnings = missingSectionIIRows(initialValuationParams, abt2Data).map((row) => {
        const value = row[valueParameter] as number | undefined;
        const percentage = row[VALUE_LINES[0].percentage] as number | undefined;
        return {
            row,
            text: t("object.tab_register.warning", {
                subtype: row[subtypeParameter],
                value:
                    value != null
                        ? formatPrice(value, language)
                        : percentage != null
                          ? `${formatValue(percentage, language)} %`
                          : "",
            }),
        };
    });

    const saveRows = (rows: Abt2Row[], changedField?: string) => dispatch(saveSectionIIRows(rows, changedField));

    function updateCell(value: unknown, field: string, index: number, dependent: Abt2Row = {}) {
        const row = rows[index];
        const current = row[field];
        if ((current == null && (value === "" || value == null)) || current === value) return;
        const changes = {[field]: value, ...dependent};
        if (row.newRow) {
            saveRows([...abt2Data, changes]);
            return;
        }
        const changed = abt2Data.map((item, rowIndex) => (rowIndex === index ? {...item, ...changes} : item));
        saveRows(changed, Object.keys(dependent).length > 0 ? undefined : field);
    }

    const addWarningRow = (row: Abt2Row) => saveRows([...abt2Data, row]);

    const addComment = (comment: string | null) => {
        void setAbt2Values(abt2Values?.activeParameterValue, comment);
    };

    const deleteRow = (index: number, row: Abt2Row) => {
        if (isNotEditable(row)) return;
        if (!isManualSectionIIEntry(row) && row[subtypeParameter] === PREEMPTION_ENTRY_NAME) {
            void setPreemptionRight(0, null);
            return;
        }
        rowKeys.remove(index);
        saveRows(abt2Data.filter((_, rowIndex) => rowIndex !== index));
    };

    const typeColumn = columns?.find((column) => column.parameter === typeParameter);
    if (!columns || !typeColumn) return null;

    const getColumnHeader = (parameter: string) => {
        const column = columns.find((item) => item.parameter === parameter);
        return column ? getByLanguage(column.details?.nameDe ?? "", column.details?.nameEn ?? "") : "";
    };

    if (compact) {
        const compactColumns = columns.filter((column) =>
            [subtypeParameter, valueParameter].includes(column.parameter),
        );
        return (
            <SectionIIValueOutput
                columns={compactColumns}
                rows={abt2Data}
                results={results}
                appraisalDetails={appraisalDetails}
                withPercentages={withPercentages}
            />
        );
    }

    const onReset = (newValue: unknown) => dispatch(resetOrEmptySectionII(newValue));
    const actionHeader = !readOnly && <th style={ROW_ACTION_COLUMN_STYLE} />;
    const labels = {
        value: getColumnHeader(valueParameter),
        text: getColumnHeader(textParameter),
        note: getColumnHeader(noteParameter),
    };

    return (
        <>
            <div
                id="input_register_section_ii"
                className="p-datatable p-component p-datatable-sm p-inputtext-sm text-sm inputtext-compact register-table w-full"
            >
                <div className="p-datatable-wrapper">
                    <table className="p-datatable-table w-full">
                        <thead className="p-datatable-thead">
                            <tr>
                                {actionHeader}
                                <th className="whitespace-nowrap">{getColumnHeader(parcelParameter)}</th>
                                <th className="whitespace-nowrap min-w-[9rem]">{getColumnHeader(nrParameter)}</th>
                                <th className="w-1/2">
                                    <ColumnHeaderWithIcons
                                        onReset={onReset}
                                        title={getColumnHeader(typeParameter)}
                                        isActive={!readOnly}
                                        comment={abt2Values?.comment}
                                        addComment={addComment}
                                        showReset={canReset}
                                        parameter={valueParameter}
                                        parameterType="REGISTER"
                                        label={t("object.section_ii_label")}
                                    />
                                </th>
                                <th className="w-1/2">{getColumnHeader(subtypeParameter)}</th>
                                <th className="!text-right">{labels.value}</th>
                            </tr>
                            {withPercentages && (
                                <tr className="section-ii-subheader">
                                    {actionHeader}
                                    <th />
                                    <th className="!text-right">{t("object.tab_register.section_ii.category")}</th>
                                    <th className="!text-right">{t("object.tab_register.section_ii.percentage")}</th>
                                    <th className="!text-right">{t("object.tab_register.section_ii.base")}</th>
                                    <th />
                                </tr>
                            )}
                        </thead>
                        {displayOrder(rows).map((index) => (
                            <SectionIIEntry
                                key={rowKeys.keyOf(rows[index], index)}
                                row={rows[index]}
                                result={results[index]}
                                labels={labels}
                                readOnly={readOnly}
                                withPercentages={withPercentages}
                                onChange={(field, value, dependent) => updateCell(value, field, index, dependent)}
                                onDelete={() => deleteRow(index, rows[index])}
                            />
                        ))}
                    </table>
                </div>
                <div className="p-datatable-footer">
                    <SectionIISumFooter
                        entries={abt2Data}
                        details={withPercentages ? (appraisalDetails ?? {}) : undefined}
                    />
                </div>
            </div>
            {warnings.map((item) => (
                <Message
                    severity="warn"
                    key={String(item.row[subtypeParameter])}
                    className={readOnly ? "w-full" : "w-full cursor-pointer"}
                    text={item.text}
                    onClick={readOnly ? undefined : () => addWarningRow(item.row)}
                />
            ))}
        </>
    );
}

interface SectionIIEntryProps {
    row: Abt2Row;
    result: SectionIIEntryResult | undefined;
    labels: {value: string; text: string; note: string};
    readOnly: boolean;
    withPercentages: boolean;
    onChange: (field: string, value: unknown, dependent?: Abt2Row) => void;
    onDelete: () => void;
}

function SectionIIEntry({row, result, labels, readOnly, withPercentages, onChange, onDelete}: SectionIIEntryProps) {
    const language = useLanguage();
    const {t} = useTranslation();
    const action = (content?: ReactNode) =>
        !readOnly && (
            <td className="section-ii-action" style={ROW_ACTION_COLUMN_STYLE}>
                {content}
            </td>
        );
    const cellProps = (field: string) => ({
        rowData: row,
        field,
        disabled: readOnly,
        onChange: (value: unknown) => onChange(field, value),
    });

    const [expanded, setExpanded] = useState(() => !isValueNeutral(row, result));
    const toggle = (
        <Button
            type="button"
            icon={expanded ? PrimeIcons.CHEVRON_DOWN : PrimeIcons.CHEVRON_RIGHT}
            text
            rounded
            severity="secondary"
            className="section-ii-toggle"
            aria-expanded={expanded}
            aria-label={t(`object.tab_register.section_ii.${expanded ? "collapse" : "expand"}`)}
            onClick={() => setExpanded(!expanded)}
        />
    );

    return (
        <tbody className="p-datatable-tbody section-ii-entry">
            <tr>
                {action(!isNotEditable(row) && <CellInputActionIcon rowData={row} onDelete={onDelete} />)}
                <td>
                    <CellInputText {...cellProps(parcelParameter)} />
                </td>
                <td>
                    <CellInputText {...cellProps(nrParameter)} />
                </td>
                <td>
                    <CellInputType {...cellProps(typeParameter)} options={abt2TypeList} />
                </td>
                <td>
                    <CellInputType {...cellProps(subtypeParameter)} options={abt2SubtypeList} />
                </td>
                <td className="text-right">
                    {!withPercentages && (
                        <CellInputNumber
                            {...cellProps(valueParameter)}
                            label={labels.value}
                            invalid={!row.newRow && row[valueParameter] == null}
                        />
                    )}
                </td>
            </tr>
            {!row.newRow && (
                <>
                    <tr className="section-ii-text">
                        {action()}
                        <td colSpan={3}>
                            <InputTextAreaField
                                value={row[textParameter] as string | null}
                                placeholder={labels.text}
                                disabled={readOnly}
                                onChange={(value) => onChange(textParameter, value)}
                            />
                        </td>
                        <td colSpan={2}>
                            <InputTextAreaField
                                value={row[noteParameter] as string | null}
                                placeholder={labels.note}
                                disabled={readOnly}
                                onChange={(value) => onChange(noteParameter, value)}
                            />
                        </td>
                    </tr>
                    {withPercentages &&
                        (expanded ? (
                            VALUE_LINES.map((line, lineIndex) => (
                                <SectionIIValueLine
                                    key={line.key}
                                    line={line}
                                    row={row}
                                    result={result}
                                    toggle={lineIndex === 0 ? toggle : <span className="section-ii-toggle" />}
                                    valueLabel={labels.value}
                                    readOnly={readOnly}
                                    onChange={onChange}
                                />
                            ))
                        ) : (
                            <tr className="section-ii-detail section-ii-collapsed">
                                {action()}
                                <td className="section-ii-indent whitespace-nowrap">
                                    {toggle}
                                    {t("object.tab_register.section_ii.deduction")}
                                </td>
                                <td colSpan={4} className="text-right">
                                    {VALUE_LINES.map(
                                        (line) =>
                                            `${t(`object.tab_register.section_ii.${line.key}`)}: ${
                                                formatSectionIIOutputValue(line.key, row, result, language) ??
                                                t("object.tab_register.no_entry")
                                            }`,
                                    ).join(" · ")}
                                </td>
                            </tr>
                        ))}
                </>
            )}
        </tbody>
    );
}

interface SectionIIValueLineProps {
    line: ValueLine;
    row: Abt2Row;
    result: SectionIIEntryResult | undefined;
    toggle: ReactNode;
    valueLabel: string;
    readOnly: boolean;
    onChange: (field: string, value: unknown, dependent?: Abt2Row) => void;
}

/** Prozentsatz und Wertansatz schließen sich aus, die Eingabe des einen leert den anderen. */
function SectionIIValueLine({line, row, result, toggle, valueLabel, readOnly, onChange}: SectionIIValueLineProps) {
    const language = useLanguage();
    const {t} = useTranslation();
    const disabled = readOnly || isNotEditable(row, line.value);
    const category = lineCategory(line, row, result);
    const hasPercentage = row[line.percentage] != null;
    const computed = result?.[line.resultValue];
    const formattedComputed = computed != null ? formatPrice(computed, language) : undefined;
    const placeholder = formattedComputed && `${t("object.associatedOutputParamPrefix")} ${formattedComputed}`;
    const isMarket = line.key === "market";

    return (
        <tr className={`section-ii-detail section-ii-${line.key}`}>
            {!readOnly && <td className="section-ii-action" />}
            <td className="section-ii-indent whitespace-nowrap">
                {toggle}
                {t(`object.tab_register.section_ii.${line.key}`)}
            </td>
            <td>
                <Dropdown
                    value={category}
                    options={line.categories.map((value) => ({
                        value,
                        label: t(`object.tab_register.section_ii.category_${value}`),
                    }))}
                    disabled={disabled}
                    className="w-full"
                    onChange={(e) => onChange(line.category, e.value)}
                />
            </td>
            <td>
                <InputNumber
                    value={(row[line.percentage] as number | null | undefined) ?? null}
                    locale={language}
                    className="!w-full"
                    inputClassName="w-full text-right"
                    aria-label={`${t(`object.tab_register.section_ii.${line.key}`)} ${t("object.tab_register.section_ii.percentage")}`}
                    placeholder={t("object.tab_register.section_ii.percentage")}
                    min={0}
                    max={100}
                    maxFractionDigits={2}
                    suffix=" %"
                    disabled={disabled}
                    onValueChange={(e) =>
                        onChange(line.percentage, e.value, row[line.value] != null ? {[line.value]: null} : undefined)
                    }
                />
            </td>
            <td>
                <Dropdown
                    value={row[line.base] ?? (hasPercentage ? DEFAULT_PERCENTAGE_BASE : null)}
                    options={PERCENTAGE_BASES.map((value) => ({
                        value,
                        label: t(`object.tab_register.section_ii.base_${value}`),
                    }))}
                    placeholder={t("object.tab_register.section_ii.base")}
                    disabled={disabled}
                    className="w-full"
                    onChange={(e) => onChange(line.base, e.value)}
                />
            </td>
            <td className="text-right">
                <CellInputNumber
                    rowData={row}
                    field={line.value}
                    disabled={readOnly}
                    label={isMarket ? valueLabel : `${valueLabel} ${t("object.tab_register.section_ii.lending")}`}
                    invalid={
                        isMarket &&
                        !disabled &&
                        category !== SectionIICategory.NOT_CONSIDERED &&
                        row[line.value] == null &&
                        !hasPercentage
                    }
                    placeholder={placeholder}
                    onChange={(value) =>
                        onChange(
                            line.value,
                            value,
                            value != null && (hasPercentage || row[line.base] != null)
                                ? {[line.percentage]: null, [line.base]: null}
                                : undefined,
                        )
                    }
                />
            </td>
        </tr>
    );
}

interface CellProps {
    rowData: Abt2Row;
    field: string;
    disabled: boolean;
    onChange: (value: unknown) => void;
}

function CellInputText({rowData, field, disabled, onChange}: CellProps) {
    return (
        <InputTextField
            onChange={(e) => onChange(e.target.value)}
            value={rowData[field]}
            className="!w-full"
            disabled={disabled}
        />
    );
}

function CellInputType({rowData, field, options, disabled, onChange}: CellProps & {options: string[]}) {
    const value = rowData[field] as string | undefined;
    return (
        <Dropdown
            value={value}
            disabled={isNotEditable(rowData, field) || disabled}
            options={options}
            invalid={!rowData.newRow && !value}
            className="w-full"
            style={{minWidth: "8rem"}}
            onChange={(e) => onChange(e.value)}
            placeholder={value}
        />
    );
}

interface CellInputNumberProps extends CellProps {
    label: string;
    invalid: boolean;
    placeholder?: string;
}

function CellInputNumber({rowData, field, disabled, onChange, label, invalid, placeholder}: CellInputNumberProps) {
    const language = useLanguage();
    return (
        <CurrencyInputNumber
            value={(rowData[field] as number | null | undefined) ?? null}
            locale={language}
            className="!w-32"
            inputClassName="w-full"
            aria-label={label}
            onValueChange={(e) => onChange(e.value)}
            disabled={isNotEditable(rowData, field) || disabled}
            invalid={invalid}
            placeholder={placeholder}
        />
    );
}

interface InputTextAreaFieldProps {
    value: string | null | undefined;
    placeholder: string;
    onChange: (value: string | null) => void;
    disabled: boolean;
}

function InputTextAreaField({value, placeholder, onChange, disabled}: InputTextAreaFieldProps) {
    const [localValue, setLocalValue] = useState(value);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    return (
        <InputTextarea
            value={localValue ?? ""}
            autoResize
            className="w-full"
            placeholder={placeholder}
            aria-label={placeholder}
            disabled={disabled}
            rows={1}
            onBlur={() => onChange(localValue || null)}
            onChange={(e) => setLocalValue(e.target.value)}
        />
    );
}

function isNotEditable(row: Abt2Row, parameter?: string): boolean {
    if (row.newRow || isManualSectionIIEntry(row)) return false;
    const subtype = row[subtypeParameter];
    if (subtype === LEASEHOLD_ENTRY_NAME) return parameter == null || liveEntryFields.includes(parameter);
    return subtype === PREEMPTION_ENTRY_NAME && parameter != null && liveEntryFields.includes(parameter);
}

function SectionIISumFooter({entries, details}: {entries: Abt2Row[]; details?: Record<string, unknown>}) {
    const {t} = useTranslation();
    const language = useLanguage();
    const sumRow = (label: ReactNode, value: unknown, className = "font-bold") => (
        <div className={`w-full flex flex-row justify-end items-center ${className}`}>
            <span className="w-80">{label}</span>
            <span className="w-36 text-right">{formatPrice(value as number, language)}</span>
        </div>
    );

    if (!details) {
        const inputSum = sumSectionIIEntryValues(
            entries as Array<Record<string, number | null | undefined>>,
            valueParameter,
        );
        return <div className="inputtext-compact">{sumRow(<>&Sigma;</>, inputSum)}</div>;
    }
    // Mit Prozentsätzen und Kategorien rechnet nur die AVM die Summe richtig (#19602)
    return (
        <div className="inputtext-compact">
            {details.section_ii_sum != null &&
                sumRow(t("object.tab_register.section_ii.sum_market"), details.section_ii_sum)}
            {details.section_ii_sum_lending != null &&
                sumRow(t("object.tab_register.section_ii.sum_lending"), details.section_ii_sum_lending)}
            {!!details.section_ii_prior_charges_lending &&
                sumRow(t("object.tab_register.section_ii.prior_charges"), details.section_ii_prior_charges_lending, "")}
        </div>
    );
}

/** Ausgabe ohne Prozentsätze in der Spec: nur der Wertansatz. */
function withMarketValues(
    abt2Data: Abt2Row[],
    results: Array<SectionIIEntryResult | undefined>,
    language: RevaLanguage,
) {
    return abt2Data.map((row, index) => ({
        ...row,
        [valueParameter]: formatSectionIIOutputValue("market", row, results[index], language),
    }));
}

/** Abt.-II-Tabelle mit Marktwert und Beleihungswert statt des Wertansatzes, Summen im Footer. */
function sectionIIValueTable(
    columns: RevaSpecObjectInputRow[],
    abt2Data: Abt2Row[],
    results: Array<SectionIIEntryResult | undefined>,
    appraisalDetails: Record<string, unknown> | undefined,
    t: TFunction,
    language: RevaLanguage,
) {
    const [marketLine, lendingLine] = VALUE_LINES;
    const valueColumn = columns.find((column) => column.parameter === valueParameter);
    if (!valueColumn) return null;

    const relabeled = (parameter: string, key: ValueLine["key"]): RevaSpecObjectInputRow => {
        const label = t(`object.tab_register.section_ii.${key}`);
        return {
            ...valueColumn,
            parameter,
            details: valueColumn.details && {...valueColumn.details, parameter, nameDe: label, nameEn: label},
        };
    };
    const mainColumns = [
        ...columns.filter((column) => column !== valueColumn),
        relabeled(marketLine.value, marketLine.key),
        relabeled(lendingLine.value, lendingLine.key),
    ];
    const mainValues = abt2Data.map((row, index) => ({
        ...row,
        [marketLine.value]: formatSectionIIOutputValue(marketLine.key, row, results[index], language),
        [lendingLine.value]: formatSectionIIOutputValue(lendingLine.key, row, results[index], language),
    }));
    const sum = (value: unknown) => (typeof value === "number" ? formatPrice(value, language) : "");
    const priorCharges = appraisalDetails?.section_ii_prior_charges_lending;
    const labelStart = Math.max(
        mainColumns.findIndex((column) => column.parameter === subtypeParameter),
        0,
    );
    const footerClassName = `p-align-right ${VALUE_CELL_CLASS}`;
    const footerGroup = (
        <ColumnGroup>
            <Row>
                {labelStart > 0 && <Column colSpan={labelStart} />}
                <Column footer="Σ" colSpan={mainColumns.length - 2 - labelStart} />
                <Column footer={sum(appraisalDetails?.section_ii_sum)} footerClassName={footerClassName} />
                <Column footer={sum(appraisalDetails?.section_ii_sum_lending)} footerClassName={footerClassName} />
            </Row>
            {!!priorCharges && (
                <Row>
                    {labelStart > 0 && <Column colSpan={labelStart} />}
                    <Column
                        footer={t("object.tab_register.section_ii.prior_charges")}
                        colSpan={mainColumns.length - 1 - labelStart}
                    />
                    <Column footer={sum(priorCharges)} footerClassName={footerClassName} />
                </Row>
            )}
        </ColumnGroup>
    );

    return {
        columns: mainColumns,
        values: mainValues,
        footerColumnGroup: footerGroup,
        valueParameters: [marketLine.value, lendingLine.value],
    };
}

interface SectionIIValueOutputProps {
    columns: RevaSpecObjectInputRow[];
    rows: Abt2Row[];
    results: Array<SectionIIEntryResult | undefined>;
    appraisalDetails: Record<string, unknown> | undefined;
    withPercentages: boolean;
    footer?: ReactNode;
}

/** Wertetabelle der Ausgabe; ohne Prozentsätze in der Spec (TODO #19602) nur der Wertansatz mit Summe. */
function SectionIIValueOutput({
    columns,
    rows,
    results,
    appraisalDetails,
    withPercentages,
    footer,
}: SectionIIValueOutputProps) {
    const {t} = useTranslation();
    const language = useLanguage();
    const valueTable = withPercentages && sectionIIValueTable(columns, rows, results, appraisalDetails, t, language);
    if (valueTable) return <RegisterOutputTable {...valueTable} />;
    return (
        <RegisterOutputTable
            columns={columns}
            values={withMarketValues(rows, results, language)}
            valueParameters={[valueParameter]}
            footer={footer}
        />
    );
}

interface RegisterOutputSectionIIProps {
    columns: RevaSpecObjectInputRow[] | undefined;
}

export function RegisterOutputSectionII({columns}: RegisterOutputSectionIIProps) {
    const indicationData = useSelector(selectIndicationData);
    // TODO #19602: Weiche für LIVE, nach dem AVM-Release entfernen
    const withPercentages = useSelector(selectHasSectionIIPercentages);
    const {parameterValues: registerData} = useParameterValue("register_abt2", "REGISTER");
    const appraisalDetails = getAppraisalValueDetails(indicationData);
    const abt2Data: Abt2Row[] = withLiveLeaseholdEntries(registerData?.activeParameterValue, appraisalDetails);
    if (!columns) return null;
    const {main: columnsMain, text: columnsText} = splitSectionIIColumns(columns);
    const abt2DataWithText = abt2Data.filter(
        (item) =>
            item[subtypeParameter] != null &&
            columnsText.some((column) => column.parameter !== subtypeParameter && item[column.parameter] != null),
    );
    const results = matchSectionIIResults(
        abt2Data,
        appraisalDetails?.section_ii_entries as SectionIIEntryResult[] | undefined,
    );

    return (
        <div>
            <SectionIIValueOutput
                columns={columnsMain}
                rows={abt2Data}
                results={results}
                appraisalDetails={appraisalDetails}
                withPercentages={withPercentages}
                footer={<SectionIISumFooter entries={abt2Data} />}
            />
            <RegisterOutputTable columns={columnsText} values={abt2DataWithText} />
        </div>
    );
}
